import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { db } from '@/lib/local-api';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import type { WorkoutRecord } from '~/powersync/generated/schema';

export interface UseWorkoutActionsResult {
  startWorkout: (workoutId: string) => Promise<void>;
  stopWorkout: (workoutId: string) => Promise<void>;
  isStarting: boolean;
  isStopping: boolean;
}

/**
 * Hook for managing workout start/stop functionality
 */
export const useWorkoutActions = (): UseWorkoutActionsResult => {
  const powerSync = usePowerSync();
  const router = useRouter();

  const startWorkoutMutation = useMutation({
    mutationFn: async (workoutId: string) => {
      // Clone the workout and start the new one
      const newWorkoutId = await db.Workout.mutations.cloneAndStartWorkout(
        powerSync,
        workoutId,
      );
      return newWorkoutId;
    },
  });

  const stopWorkoutMutation = useMutation({
    mutationFn: async (workoutId: string) => {
      const now = new Date().toISOString();
      await db.Workout.mutations.update(powerSync, workoutId, {
        finishedAt: now,
      });
    },
  });

  const startWorkout = async (workoutId: string) => {
    const newWorkoutId = await startWorkoutMutation.mutateAsync(workoutId);

    // Get the workout's niceId to redirect to progress
    const newWorkout = await powerSync
      .selectFrom('Workout')
      .select('niceId')
      .where('id', '=', newWorkoutId)
      .executeTakeFirst();

    if (newWorkout?.niceId) {
      router.replace(`/workouts/${newWorkout.niceId}/progress`);
    }
  };

  const stopWorkout = async (workoutId: string) => {
    await stopWorkoutMutation.mutateAsync(workoutId);
  };

  return {
    startWorkout,
    stopWorkout,
    isStarting: startWorkoutMutation.isPending,
    isStopping: stopWorkoutMutation.isPending,
  };
};
